import { ReactNode } from 'react'
import { Link } from 'react-router-dom'

/**
 * MobileFloatingCTA — CTA primaire flottant en zone pouce (mobile-only)
 *
 * Affiche un bouton/lien fixe en bas d'écran sur mobile uniquement (`md:hidden`),
 * positionné dans la zone pouce naturelle, avec safe-area iOS.
 *
 * Conditions d'usage : listes longues (≥ 6 items) où le CTA primaire est
 * éloigné après scroll (page `/offres`, `/disponibles`).
 *
 * - Hauteur min 48px (≥ norme Apple HIG 44px).
 * - Zone d'évitement : ajouter `pb-24 md:pb-0` au contenu parent quand actif,
 *   ou compter sur le footer naturel si la liste se termine assez loin.
 * - Désactivé automatiquement si le composant `MobileBottomNav` est aussi
 *   monté (dashboards) — à ne pas combiner.
 */
export interface MobileFloatingCTAProps {
  /** Cible interne (Link react-router) ou externe (a href). */
  to?: string
  href?: string
  /** Handler au clic (alternative à to/href). */
  onClick?: () => void
  /** Contenu visible : icône + label. */
  children: ReactNode
  /** Variante de couleur (défaut: blue). */
  variant?: 'blue' | 'red' | 'emerald' | 'gray'
  /** Mode visuel : 'solid' (par défaut) ou 'glass' (dégradé translucide). */
  visual?: 'solid' | 'glass'
  /** Type de bouton si onClick (défaut: button). */
  type?: 'button' | 'submit'
  /** Désactiver l'affichage (ex: liste vide ou < seuil). */
  hidden?: boolean
  /** Label aria si le texte visible n'est pas explicite. */
  ariaLabel?: string
}

const VARIANT_CLASSES: Record<NonNullable<MobileFloatingCTAProps['variant']>, string> = {
  blue: 'bg-blue-600 hover:bg-blue-700 text-white shadow-blue-600/40',
  red: 'bg-red-600 hover:bg-red-700 text-white shadow-red-600/40',
  emerald:
    'bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white shadow-emerald-600/40',
  gray: 'bg-gray-900 hover:bg-gray-800 text-white shadow-gray-900/40',
}

export default function MobileFloatingCTA({
  to,
  href,
  onClick,
  children,
  variant = 'blue',
  visual = 'solid',
  type = 'button',
  hidden = false,
  ariaLabel,
}: MobileFloatingCTAProps) {
  if (hidden) return null

  const baseClasses = `inline-flex items-center justify-center gap-2 w-full min-h-[52px] px-5 rounded-2xl font-bold text-sm transition-all active:scale-[0.98] shadow-2xl ${VARIANT_CLASSES[variant]}`

  const wrapperClasses =
    'md:hidden fixed left-0 right-0 bottom-0 z-40 px-4 pt-2 pb-[max(0.75rem,env(safe-area-inset-bottom))] pointer-events-none'

  const innerWrapper =
    visual === 'glass'
      ? 'pointer-events-auto bg-white/85 backdrop-blur-md border border-gray-200 rounded-2xl shadow-[0_-4px_20px_rgba(15,23,42,0.08)] p-2'
      : 'pointer-events-auto'

  const content = (
    <span className="inline-flex items-center justify-center gap-2 w-full">{children}</span>
  )

  const button = to ? (
    <Link to={to} className={baseClasses} aria-label={ariaLabel}>
      {content}
    </Link>
  ) : href ? (
    <a href={href} className={baseClasses} aria-label={ariaLabel}>
      {content}
    </a>
  ) : (
    <button
      type={type}
      onClick={onClick}
      className={baseClasses}
      aria-label={ariaLabel}
    >
      {content}
    </button>
  )

  return (
    <div className={wrapperClasses} role="region" aria-label="Action principale">
      <div className={innerWrapper}>{button}</div>
    </div>
  )
}
